// useVesselNamingText.ts
import { useEffect, useRef, useState } from 'react';
import { axesLineRenderer, updateRenderWindow, useBufferPool, VRContext } from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import {
  getLabelVolumeFromSeriesId,
  getRequestSegmentation,
  getViewportNegative,
} from '@features/analysis/rendering/Panels/Renderers/CornerstoneManager';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { useConfig } from 'config/useConfig';
import { initCameraForAnalysis, runDilateDataVolume, runSegmentationVolume, updateBaseVolumeMapper, updateCutter, updateEFVolumeMapper, updateVolumeColor } from '@features/view/VRUtilities';
import useModalActions from '@hooks/common/useModalAction';
import { getSegShapeAnalysisType, getSegVolumeType } from 'config/analysisConfig';
import { scheduleRender } from '@features/analysis/rendering/Panels/renderState';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import { setActorVisible } from '@features/view/Shared3DUtilities';
import { startVrLoadPerf } from '@features/view/vrLoadPerf';
import vtkVolume from '@kitware/vtk.js/Rendering/Core/Volume';
import { nodeNames, volumeKeys, volumeNames } from 'config/render3DConfig';

type useSegmentationVolumeParams = {
  context: React.MutableRefObject<VRContext | null>,
  chamberViewRef: React.MutableRefObject<{
    viewNormal: any;
    normal: any;
    centers: any;
    index: any;
  }>;
  lowQuality: boolean;
};

export function useSegmentationVolume({
  context,
  chamberViewRef,
  lowQuality,
}: useSegmentationVolumeParams) {

  const { selectedSeriesId, seriesSelections, selectedStudySeries } = useBrowserPage();

  const {
    analysisType,
    panelStates,
    selectedPanelComponentKey,
    updateSelectedPanelConfig,
  } = usePanelContainer();

  const {
    selectedLayoutConfig,
    currentVOI,
    globalToolState
  } = useAnalysisPage();

  const { bufferPool, setBufferPool } = useBufferPool();
  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;
  const { segmentation } = panelStates['Surface3D'];
  const { config, setData } = useConfig();
  const { waitingAction, closeLoadingModal, errorDefaultAction } =
    useModalActions();
  const renderingQuality = config.generalSetting.renderingQuality;
  const {windowLevel,windowWidth,invert} = currentVOI;
  const isSegmentationShape = getSegShapeAnalysisType(analysisType);
  const [baseVolumeReady, setBaseVolumeReady] = useState(false);
  const isRunning = useRef(false);
  const markBaseVolumeReady = () => {
    if (!context.current) return;
    const { actorIds, renderer } = context.current;
    const targetVolume = actorIds[volumeNames.base];
    const exists = renderer.getVolumes().some(v => v === targetVolume);
    if(exists){
      setBaseVolumeReady(true);
    }
  };

  // function getLowestEFSeriesId(
  //   efMap: Record<string, { percent: number; data: Uint8Array }>,
  //   seriesSelections?: { id: string; percent: string }[],
  // ) {
  //   let selectedId: string | undefined;
  //   let selectedPercent = Infinity;

  //   for (const selection of seriesSelections ?? []) {
  //     if (!efMap[selection.id]) continue;

  //     const percent = Number(selection.percent);
  //     if (!Number.isFinite(percent)) continue;

  //     if (percent < selectedPercent) {
  //       selectedId = selection.id;
  //       selectedPercent = percent;
  //     }
  //   }

  //   if (selectedId) {
  //     return { id: selectedId, percent: selectedPercent, source: 'selection' };
  //   }

  //   let fallbackId: string | undefined;
  //   let fallbackPercent = Infinity;

  //   for (const [id, value] of Object.entries(efMap)) {
  //     if (!Number.isFinite(value.percent)) continue;

  //     if (value.percent < fallbackPercent) {
  //       fallbackId = id;
  //       fallbackPercent = value.percent;
  //     }
  //   }

  //   return { id: fallbackId, percent: fallbackPercent, source: 'efMap' };
  // }

  function createVolume(){
    const finishPerf = startVrLoadPerf('VRPanel.useSegmentationVolume.createVolume', {
      analysisType,
      renderingQuality,
      seriesId: selectedSeriesId,
    });
    try {
    if (!context.current || !pool || !selectedSeriesId) return;

    context.current.isAxis3DVisible = globalToolState?.toggles.ToggleAxes!;

    if (getSegVolumeType(analysisType)) {
      createSegVolume();
    }
    else if(analysisType === 'ejectionFraction'){
      return;
      //  const efMap = pool?.EF?.data as
      //   | Record<string, { percent: number; data: Uint8Array }>
      //   | undefined;

      // if(!efMap) return;

      // const { id: useId, percent } = getLowestEFSeriesId(efMap, seriesSelections);
      // if (!useId || efMap[useId] === undefined) return;
      // createEFVolume(useId);
    }else{
      updateBaseVolumeMapper(
        context,
        pool,
        selectedSeriesId!,
        analysisType,
        undefined,
        {renderingQuality}
      );
      axesLineRenderer(context);
      markBaseVolumeReady();
    }

    if(analysisType !== 'ejectionFraction'){
      const camera = context.current.renderer.getActiveCamera();
      const focalPoint = camera.getFocalPoint();
      const distance = camera.getDistance();
      camera.setPosition(focalPoint[0], focalPoint[1] + distance, focalPoint[2]);
      camera.setViewUp(0, 0, -1);
    }
    context.current.axes =  {
      x: [1, 0, 0],
      y: [0, 1, 0],
      z: [0, 0, 1],
    };
    } finally {
      finishPerf();
    }
  }

  function createEFVolume(
    useId: string | undefined,
  ){
    if (!context.current || !pool || !selectedSeriesId) return;
    const {renderer,renderWindow} = context.current;
    const efMap = pool?.EF?.data as
      | Record<string, { percent: number; data: Uint8Array }>
      | undefined;
    if(!efMap || !useId) return;
    if(efMap[useId] === undefined){
      for(let i =0; i < renderer.getVolumes().length;i++)
        renderer.getVolumes()[i].setVisibility(false);
      renderWindow.render();
      console.log('VR Panel ef 없대',efMap,useId);
      return;
    }
    context.current.efId = useId;
    setData({rrPercent_ef: efMap[useId].percent});

    const res = updateEFVolumeMapper(
      context,
      pool,
      analysisType,
      selectedSeriesId,
      useId,
      {renderingQuality}
    );

    if(!res) return;
    const { centers, viewNormal, normal } = res;
    const world = context.current?.focus;

    if(world)
      scheduleRender('Axial', { type: 'focus', world,request:true});


    axesLineRenderer(context);
    chamberViewRef.current = {
      viewNormal,
      normal,
      centers,
      index: 0,
    };
  }

  async function createSegVolume(){
    if (!context.current || !pool || !selectedSeriesId) return;
    const { actorIds, renderer } = context.current;
    const segVolume = getLabelVolumeFromSeriesId(
      selectedSeriesId!,
      lowQuality
    );
    if(actorIds['volume'] || !segVolume) {
      return;
    }
    await updateSegVolume({});
    const targetVolume = actorIds[volumeNames.calcium] as vtkVolume;
    if(targetVolume && analysisType === 'plaque' && !config.visibleMIP){
      const exists = renderer.getVolumes().some(v => v === targetVolume);
      //무조건 새로 만들때는 true
      //study list에서 같은 시리즈로 다시 돌아갔을때 panelStates['Surface3D'].segmentation 바로 갱신이 안댐.
      const seg = getRequestSegmentation(analysisType, segmentation!);
      const visibility = (seg['calcium'].visibility);
      targetVolume.setVisibility(visibility);
      if(!exists){
        //혹시나 하는 예외처리
        console.log('없어서 넣었어');
        renderer.addVolume(targetVolume);
      }
    }

    if(analysisType !== 'chamber'){
      initCameraForAnalysis(
        context,
        analysisType,
        selectedSeriesId!,
        pool,
      );
    }
  }

  async function updateSegVolume({
    WL,
    visibleKeys,
    refreshHeartCut = false,
    refreshMIP = false,
  }: {
    WL?: number[];
    visibleKeys?: string[];
    refreshHeartCut?: boolean;
    refreshMIP?: boolean;
  }) {
    const finishPerf = startVrLoadPerf('VRPanel.useSegmentationVolume.updateSegVolume', {
      analysisType,
      heartCut: refreshHeartCut,
      renderingQuality,
      seriesId: selectedSeriesId,
      visibleKeyCount: visibleKeys?.length,
      visibleMIP: refreshMIP,
    });
    const run = async () => {
      if(!context.current || !selectedSeriesId || !pool) return;
      const { actorIds, renderer } = context.current;
      if(!visibleKeys){
        const seg = getRequestSegmentation(analysisType, segmentation!);
        const keys = Object.keys(seg) as (keyof typeof seg)[];
        visibleKeys = keys.filter((key) => seg[key].visibility);
      }

      if(visibleKeys.length === 0 && analysisType !== 'chamber'){
       console.log('모두 off');
       for (const [key, actor] of Object.entries(actorIds)) {
         if ((actor as any).isA?.('vtkVolume')) {
           actor.setVisibility(false);
         }
       }

       if(actorIds[volumeNames.calcium]){
        const seg = getRequestSegmentation(analysisType, segmentation!);
        const visibility = seg['calcium'].visibility;
        actorIds[volumeNames.calcium].setVisibility(visibility);
      }

       if(analysisType !== 'segmentation'){
        setActorVisible(context, false, [
          nodeNames.node,
          nodeNames.centerLine,
          nodeNames.stenosis,
        ]);
       }
       updateRenderWindow(context);
       return;
      }

      const runHeartCut =
        isSegmentationShape
          ? (refreshHeartCut ? refreshHeartCut : config.heartCut)
          : false;

      const runMIP =
        isSegmentationShape
          ? (refreshMIP? refreshMIP : config.visibleMIP)
          : false;

      await runSegmentationVolume(
        context,
        pool,
        selectedSeriesId!,
        analysisType!,
        {
          chamberEnabled:analysisType === 'chamber',
          heartCut: runHeartCut,
          visibleMIP: runMIP,
          ww: WL? WL[0] : windowWidth,
          wl: WL? WL[1] : windowLevel,
          visibleKeys,
          negativeMode: invert,
          renderingQuality:config.generalSetting.renderingQuality,
        },
        setBufferPool,
      );


      let volumeName: volumeKeys = volumeNames.base;
      if (analysisType === 'chamber') {
        const volume = actorIds[volumeName];
        if (!volume) {
          return;
        }
        const applyCutter = (chamberProps: any) => {
          initCameraForAnalysis(context, analysisType, selectedSeriesId!);
          updateCutter(context, chamberProps.index, chamberProps, true);
        };

        if (chamberViewRef.current?.centers !== null) {
          applyCutter(chamberViewRef.current);
        }
      } else if (isSegmentationShape) {
        volumeName = config.visibleMIP
          ? volumeNames.MIP
          : config.heartCut
            ? volumeNames.heartCut
            : volumeNames.base;
      }
      if (config.volumeDilation.enabled) {
        await runDilateDataVolume(context, selectedSeriesId, volumeName, true);
      }
      setSegVolumeVisibility(volumeName);
      axesLineRenderer(context);
      markBaseVolumeReady();
    };

    if (isRunning.current) {
      console.log('연달아 누르는 행위');
      finishPerf({ visibleKeyCount: visibleKeys?.length });
      return;
    }

    isRunning.current = true;
    waitingAction(
      { message: 'Waiting' },
      {
        timeoutMs: 60_000,
        onTimeout: () => {
          errorDefaultAction('The request timed out.');
        },
      },
    );
    await run().catch((error) => {
      console.error('Failed to update segmentation volume:', error);
      errorDefaultAction('Failed to update segmentation volume.');
    });
    isRunning.current = false;
    closeLoadingModal();
    finishPerf({ visibleKeyCount: visibleKeys?.length });
  }

  function ensureVolume(
    name: volumeKeys,
  ){
    if(!context.current) return;
    const { actorIds, renderer } = context.current;
    const targetVolume = actorIds[name];
    const exists = renderer.getVolumes().some(v => v === targetVolume);
    if(!exists){
      if (
        name === 'volume' ||
        (name === 'volumeHeartCut' && config.heartCut) ||
        (name === 'volumeMIP' && config.visibleMIP)
      ) {
        updateSegVolume({});
      }
    }
    return exists;
  }


  function updateVolumeMIPColor(
    windowWidth?: number,
    windowLevel?: number,
    negative?:boolean,
  ){
    if(!context.current) return;
    const negativeMode = negative?? invert;
    const exists = ensureVolume('volumeMIP');
    if(exists  && config.visibleMIP){
      updateVolumeColor(
        context,
        selectedSeriesId!,
        'MIP',
        {
          ww:windowWidth,
          wl:windowLevel,
          negativeMode,
        },
      );
    }

    //console.log(negativeMode)
    setMIPbackgroundColor({negative: negativeMode,name:'volumeMIP'});
    scheduleRender('VR', { type: 'updateRenderWindow' });
  }


  function setMIPbackgroundColor({
    name,
    negative = false,
  }: {
    name?: volumeKeys;
    negative?: boolean;
  }) {
     if(!name) return;
    const defaultBg = [0.05, 0.05, 0.05] as [number, number, number];
    const background: [number, number, number] =
      name !== 'volumeMIP'
        ? defaultBg
        : negative
        ? [0.9, 0.9, 0.9]
        : defaultBg;

    context?.current?.genericRenderWindow?.setBackground(background);
  }

  function setSegVolumeVisibility(
    name?: volumeKeys,
  ){
    if(!context.current) return;
    const { actorIds } = context.current;
    for (const [key, actor] of Object.entries(actorIds)) {
      if ((actor as any).isA?.('vtkVolume')) {
        actorIds[key].setVisibility(name ? name === key : false);
      }
    }

    const targetVolume = actorIds[volumeNames.calcium] as vtkVolume;
    if(targetVolume && analysisType === 'plaque'){
      // segmentation calcium 보고 visibility 상태확인하기
      const seg = getRequestSegmentation(analysisType, segmentation!);
      const visibility = (seg['calcium'].visibility && !config.visibleMIP);
      targetVolume.setVisibility(visibility);
    }
    setMIPbackgroundColor( { name, negative:invert } );
    scheduleRender('VR', { type: 'updateRenderWindow' });
  }

  return {
    createVolume,
    createEFVolume,
    updateSegVolume,
    updateVolumeMIPColor,
    ensureVolume,
    setSegVolumeVisibility,
    baseVolumeReady,
  };
}
